import { Alert, Button, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import type { ActivityProductDetail, ActivityProductDetailResult } from "./types";

const pricingMethodLabels: Record<number, string> = {
    1: "市场价+活动单价",
    2: "比市场价直降",
    3: "以活动现场公布为准"
};
const overbookingLabels: Record<number, string> = { 0: "否", 1: "是" };
const orderRuleLabels: Record<number, string> = { 1: "固定金额", 2: "阶梯金额" };

export interface ActivityProductDetailPageProps {
    loadProductDetail?: (code: string) => Promise<ActivityProductDetailResult>;
}

/** 复用 Vue 活动商品详情接口。 */
export async function loadActivityProductDetail(code: string): Promise<ActivityProductDetailResult> {
    const response = await request.service.get<ActivityProductDetailResult>(`/admin/product-campaign/${code}`);
    return response.data;
}

const formatNumber = (value?: number) => (value == null ? "-" : String(value));
const formatFileIds = (ids?: Array<number | string>) => (ids?.length ? ids.join("、") : "-");

/** 商品活动详情页，按 Vue 详情字段展示，不在此页改变商品状态。 */
export function ActivityProductDetailPage({
    loadProductDetail = loadActivityProductDetail
}: ActivityProductDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const code = new URLSearchParams(location.search).get("code") || "";
    const [detail, setDetail] = useState<ActivityProductDetail>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // Vue 活动商品接口的枚举可能以字符串返回，先统一为数字再决定详情分支。
    const pricingMethod = detail?.pricingMethod == null ? undefined : Number(detail.pricingMethod);
    // Vue 活动商品下单规则同样可能返回字符串，统一后再展示对应意向金结构。
    const orderRule = detail?.orderRule == null ? undefined : Number(detail.orderRule);
    // Vue 活动商品超售标记兼容带空白的字符串枚举，避免详情显示缺省值。
    const hasOverbooking = detail?.hasOverbooking == null ? undefined : Number(detail.hasOverbooking);

    useEffect(() => {
        if (!code) return;
        void loadProductDetail(code)
            .then(result => {
                setErrorMessage(null);
                setDetail(result.productCampaign);
            })
            .catch(() => setErrorMessage("活动商品详情加载失败"));
    }, [code, loadProductDetail]);

    return (
        <section aria-label="activity-product-detail-page">
            <div className="section-card">
                <ZhDetailHeader title="活动商品详情" />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <ZhGrid className="mt-6" cols={4} gap={[30, 16]}>
                    <ZhBaseItem label="商品编号" value={code || "-"} />
                    <ZhBaseItem
                        label="供应商"
                        value={
                            detail?.supplierEnterprise?.name ||
                            detail?.supplierEnterprise?.enterpriseName ||
                            detail?.supplierName ||
                            "-"
                        }
                    />
                    <ZhBaseItem label="活动商品" value={detail?.name || "-"} />
                    <ZhBaseItem label="定价方式" value={pricingMethodLabels[pricingMethod || 0] || "-"} />
                    {pricingMethod === 1 ? (
                        <>
                            <ZhBaseItem label="市场价" value={`市场价：${formatNumber(detail?.marketPrice)}元/吨`} />
                            <ZhBaseItem
                                label="活动单价"
                                value={`活动单价：${formatNumber(detail?.estimatedUnitPrice)}元/吨`}
                            />
                        </>
                    ) : null}
                    {pricingMethod === 2 ? (
                        <ZhBaseItem
                            label="比市场直降"
                            value={`比市场直降：${formatNumber(detail?.lowerThanMarketPrice)}元/吨`}
                        />
                    ) : null}
                    <ZhBaseItem label="库存量" value={`库存量：${formatNumber(detail?.stock)}吨`} />
                    <ZhBaseItem label="是否允许超售" value={overbookingLabels[hasOverbooking ?? -1] || "-"} />
                    <ZhBaseItem label="起订吨数" value={`起订吨数：${formatNumber(detail?.minOrderTon)}吨`} />
                    <ZhBaseItem
                        label="最小调整吨数"
                        value={`最小调整吨数：${formatNumber(detail?.minAdjustmentTon)}吨`}
                    />
                    <ZhBaseItem label="下单要求" value={orderRuleLabels[orderRule || 0] || "-"} />
                    {orderRule === 1 ? (
                        <ZhBaseItem label="意向金" value={`意向金：${formatNumber(detail?.intentionDeposit)}元`} />
                    ) : null}
                    {orderRule === 2 ? (
                        <ZhBaseItem
                            label="最低意向金"
                            value={`最低意向金：${formatNumber(detail?.intentionDeposit)}元（每增加${formatNumber(detail?.tonStep)}吨增加${formatNumber(detail?.amountStep)}元）`}
                        />
                    ) : null}
                    <ZhBaseItem label="图片" value={`图片文件：${formatFileIds(detail?.pictureFileIds)}`} />
                    <ZhBaseItem
                        label="视频"
                        value={`视频文件：${detail?.videoFileId == null ? "-" : String(detail.videoFileId)}`}
                    />
                    <ZhBaseItem label="商品详情" span={4}>
                        {detail?.detail ? <div dangerouslySetInnerHTML={{ __html: detail.detail }} /> : "-"}
                    </ZhBaseItem>
                </ZhGrid>
                <Space className="mt-4">
                    <Button onClick={() => void navigate(-1)}>返回</Button>
                </Space>
            </div>
        </section>
    );
}
